import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { SectionLabel } from "./ui-kit";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { ChevronRight } from "lucide-react";

export const Breadcrumbs = ({ items, dark = false }) => (
  <nav className={`flex items-center flex-wrap gap-1 font-sans text-xs ${dark ? "text-white/50" : "text-slate"}`} data-testid="breadcrumbs">
    {items.map((it, i) => (
      <span key={i} className="flex items-center gap-1">
        {i > 0 && <ChevronRight className="w-3 h-3" strokeWidth={1.5} />}
        {it.to ? (
          <Link to={it.to} className="hover:text-orange transition-colors">
            {it.name}
          </Link>
        ) : (
          <span className={dark ? "text-white" : "text-navy"}>{it.name}</span>
        )}
      </span>
    ))}
  </nav>
);

export const PageHero = ({ label, title, intro, breadcrumbs, dark = false }) => (
  <section className={`pt-36 pb-16 md:pt-44 md:pb-24 relative z-10 ${dark ? "bg-navy text-white" : ""}`}>
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      {breadcrumbs && (
        <div className="mb-8">
          <Breadcrumbs items={breadcrumbs} dark={dark} />
        </div>
      )}
      <Reveal>
        <SectionLabel dark={dark}>{label}</SectionLabel>
      </Reveal>
      <Reveal delay={0.08}>
        <h1 className={`mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl ${dark ? "text-white" : "text-navy"}`}>
          {title}
        </h1>
      </Reveal>
      {intro && (
        <Reveal delay={0.16}>
          <p className={`mt-6 font-sans text-lg max-w-2xl ${dark ? "text-white/70" : "text-slate"}`}>{intro}</p>
        </Reveal>
      )}
    </div>
  </section>
);

export const Section = ({ children, className = "", dark = false, cream = false, id }) => (
  <section
    id={id}
    className={`py-20 md:py-28 relative z-10 ${dark ? "bg-navy text-white" : cream ? "bg-cream" : "bg-white"} ${className}`}
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">{children}</div>
  </section>
);

export const SectionHead = ({ label, title, intro, dark = false, center = false }) => (
  <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl mb-14`}>
    <Reveal>
      <SectionLabel dark={dark}>{label}</SectionLabel>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className={`mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight ${dark ? "text-white" : "text-navy"}`}>
        {title}
      </h2>
    </Reveal>
    {intro && (
      <Reveal delay={0.14}>
        <p className={`mt-5 font-sans text-base md:text-lg ${dark ? "text-white/70" : "text-slate"}`}>{intro}</p>
      </Reveal>
    )}
  </div>
);

export const FAQ = ({ items, dark = false }) => (
  <Accordion type="single" collapsible className="w-full" data-testid="faq">
    {items.map((f, i) => (
      <AccordionItem key={i} value={`item-${i}`} className={dark ? "border-white/10" : "border-navy/10"}>
        <AccordionTrigger className={`text-left font-serif text-lg md:text-xl hover:no-underline ${dark ? "text-white" : "text-navy"}`}>
          {f.q}
        </AccordionTrigger>
        <AccordionContent className={`font-sans text-base leading-relaxed ${dark ? "text-white/60" : "text-slate"}`}>
          {f.a}
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

// Numbered manifesto / framework chapters
export const NumberedList = ({ items, dark = false }) => (
  <div className="space-y-px">
    {items.map((it, i) => (
      <Reveal key={i} delay={i * 0.06}>
        <div
          className={`grid md:grid-cols-12 gap-6 py-10 border-t ${dark ? "border-white/10" : "border-navy/10"} ${
            i === items.length - 1 ? (dark ? "border-b border-white/10" : "border-b border-navy/10") : ""
          }`}
        >
          <div className="md:col-span-2">
            <span className="font-serif text-4xl md:text-5xl text-orange">{String(i + 1).padStart(2, "0")}</span>
          </div>
          <div className="md:col-span-4">
            <h3 className={`font-serif text-xl md:text-2xl ${dark ? "text-white" : "text-navy"}`}>{it.title}</h3>
          </div>
          <div className="md:col-span-6">
            <p className={`font-sans text-base leading-relaxed ${dark ? "text-white/60" : "text-slate"}`}>{it.body}</p>
          </div>
        </div>
      </Reveal>
    ))}
  </div>
);
